import React, {
  FC,
  useState,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  CSSProperties,
} from "react";
import { Menu } from "composants/DropDown/Menu";
import { Fa } from "composants/Icon";
import { useDispatch } from "react-redux";
import classNames from "classnames";
import { t, formatDateRelative } from "utils/i18n";

import "./NotificationMenu.css";
import {
  AllNotificationAction,
  NotificationIntent,
  Notification,
  NotificationGroup,
} from "types/Notification";

import { compareDesc } from "date-fns";
import { getNavigationUrlFromMap } from "utils/navigation.utils";
import { Link, useNavigate } from "react-router-dom";
import { ProcessusProvider } from "composants/processus/ProcessusProvider";
import ProcessusLink from "composants/processus/ProcessusLink";
import { Tooltip } from "@axin-org/comet";
import { IconName } from "@fortawesome/pro-solid-svg-icons";
import {
  ProcessusDefinitionNature,
  ProcessusAffectation,
  ProcessusDefinition,
} from "types/Processus";
import useNotifications, { NOTIFICATION_PRIORITY_VALUE } from "hooks/useNotifications";
import { tw } from "twind";
import { useSpring, useSpringRef, useTransition, animated } from "@react-spring/web";

const EMPTY_HEIGHT = 80;

function sortNotifications(a: Notification, b: Notification) {
  const aUnread = a.validatedAt == null;
  const bUnread = b.validatedAt == null;
  if (aUnread !== bUnread) return aUnread ? -1 : 1;
  const priorityDiff =
    NOTIFICATION_PRIORITY_VALUE[b.priority] - NOTIFICATION_PRIORITY_VALUE[a.priority];
  if (priorityDiff !== 0) return priorityDiff;
  return compareDesc(new Date(a.createdAt), new Date(b.createdAt));
}

export const NotificationMenu: FC = () => {
  const [tabsState, setTabsState] = useState<NotificationGroup>(NotificationGroup.EDITION);
  const {
    notifications,
    nbUnreadNotifications,
    countUnreadByGroup,
    vibrate,
    hasMore,
    isLoading,
    loadMore,
    markAllAsRead,
    markAsRead,
  } = useNotifications(tabsState);

  const nbUnreadMetier = countUnreadByGroup[NotificationGroup.METIER] ?? 0;

  return (
    <Menu autoclose>
      <Menu.Button title={t("commun_notification")} className="system-topbar-icon-btn">
        <Fa icon={["fal", "bell"]} className={classNames("fa-lg", vibrate && "vibrate-bell")} />
        {nbUnreadMetier > 0 && (
          <span className="system-topbar-badge">
            {nbUnreadMetier > 99 ? "99+" : nbUnreadMetier}
          </span>
        )}
      </Menu.Button>
      <Menu.Overlay
        className="shadow-lg notification-scroll"
        style={{ width: 600 }}
        styleContainer={{ zIndex: 40 }}
      >
        <Menu.WhenVisible>
          {() => (
            <PanelWrapper>
              <div
                className={tw(
                  "flex justify-between items-center px-3.5 py-3 bg-white border-b border-slate-100",
                )}
              >
                <p className={tw("text-sm text-slate-500")}>
                  <strong className={tw("font-bold text-slate-900")}>
                    {countUnreadByGroup[tabsState] ?? 0}
                  </strong>{" "}
                  {t("commun_notifications_non_lues")}
                </p>
                <button
                  type="button"
                  disabled={nbUnreadNotifications === 0}
                  className={tw(
                    "inline-flex items-center gap-1.5 border-none rounded-full",
                    "px-3.5 py-1.5 text-sm font-semibold",
                    nbUnreadNotifications === 0
                      ? "bg-gray-100 text-slate-400 cursor-not-allowed"
                      : "bg-blue-600 text-white cursor-pointer hover:bg-blue-700 active:bg-blue-800",
                  )}
                  style={
                    nbUnreadNotifications > 0
                      ? { boxShadow: "0 1px 2px rgba(37,99,235,0.3)" }
                      : undefined
                  }
                  onClick={markAllAsRead}
                >
                  <Fa icon={["fas", "check"]} />
                  {t("commun_valider_tout")}
                </button>
              </div>
              <div className="tabs">
                <ul>
                  <Tab
                    tabState={tabsState}
                    currentTab={NotificationGroup.EDITION}
                    setTabState={setTabsState}
                    tabCount={countUnreadByGroup[NotificationGroup.EDITION]}
                    iconName="print"
                  >
                    {t("commun_edition")}
                  </Tab>
                  <Tab
                    tabState={tabsState}
                    currentTab={NotificationGroup.METIER}
                    setTabState={setTabsState}
                    tabCount={countUnreadByGroup[NotificationGroup.METIER]}
                    iconName="briefcase"
                  >
                    {t("commun_metier")}
                  </Tab>
                  <Tab
                    tabState={tabsState}
                    currentTab={NotificationGroup.INFO}
                    setTabState={setTabsState}
                    tabCount={countUnreadByGroup[NotificationGroup.INFO]}
                    iconName="info-square"
                  >
                    {t("commun_info")}
                  </Tab>
                  <Tab
                    tabState={tabsState}
                    currentTab={NotificationGroup.DEFAULT}
                    setTabState={setTabsState}
                    tabCount={countUnreadByGroup[NotificationGroup.DEFAULT]}
                    iconName="solar-system"
                  >
                    {t("commun_systeme")}
                  </Tab>
                </ul>
              </div>
              <MenuCards
                notifications={notifications}
                markAsRead={markAsRead}
                loadMore={loadMore}
                hasMore={hasMore}
                isLoading={isLoading}
              />
            </PanelWrapper>
          )}
        </Menu.WhenVisible>
      </Menu.Overlay>
    </Menu>
  );
};

const MenuCards: FC<{
  notifications: Notification[];
  markAsRead: (id: string) => void;
  loadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
}> = React.memo(({ notifications, markAsRead, loadMore, hasMore, isLoading }) => {
  const showLoader = useDelayedLoader(isLoading);
  const containerRef = useRef<HTMLDivElement>(null);

  // Détection du scroll infini via ResizeObserver + listener scroll natif.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || isLoading || !hasMore) return;

    function checkScroll() {
      if (!container || isLoading || !hasMore) return;
      if (container.clientHeight === 0) return;
      const { scrollHeight, clientHeight, scrollTop } = container;
      if (scrollHeight <= clientHeight || scrollTop + clientHeight >= scrollHeight - 4) {
        loadMore();
      }
    }

    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(container);
    container.addEventListener("scroll", checkScroll);

    return () => {
      resizeObserver.disconnect();
      container.removeEventListener("scroll", checkScroll);
    };
  }, [isLoading, hasMore, loadMore]);

  const sortedNotification = useMemo(() => {
    const toSort = [...notifications];
    toSort.sort(sortNotifications);
    return toSort;
  }, [notifications]);

  const heightApi = useSpringRef<{ height: number }>();
  const [heightStyle] = useSpring(() => ({ ref: heightApi, from: { height: 0 } }));

  useLayoutEffect(() => {
    if (isLoading) return;
    heightApi.stop();
    const target =
      sortedNotification.length === 0
        ? EMPTY_HEIGHT
        : Math.min(containerRef.current?.scrollHeight ?? EMPTY_HEIGHT, 400);
    heightApi.start({ height: target, config: { tension: 320, friction: 22, clamp: true } });
  }, [isLoading, sortedNotification, heightApi]);

  return (
    <animated.div style={{ ...heightStyle, minHeight: EMPTY_HEIGHT, overflow: "hidden" }}>
      {sortedNotification.length > 0 ? (
        <div ref={containerRef} className="scrollable" style={{ maxHeight: 400 }}>
          {sortedNotification.map((item) => (
            <div
              key={item.id}
              className="px-7 py-8"
              style={{
                background: item.validatedBy == null ? background[item.intent] : undefined,
                borderBottom: "1px solid hsla(0, 0%, 92%, 1)",
                borderLeft: `3px solid ${colors[item.intent]}`,
              }}
            >
              <NotificationCard
                id={item.id}
                title={item.title}
                actions={item.actions}
                isNew={item.validatedAt == null}
                createdAt={item.createdAt}
                intent={item.intent}
                validate={markAsRead}
              >
                {item.message}
              </NotificationCard>
            </div>
          ))}
          {hasMore && !isLoading && <div style={{ height: 1 }} />}
        </div>
      ) : (
        <div
          ref={containerRef}
          style={{ height: EMPTY_HEIGHT, position: "relative", overflow: "hidden" }}
        >
          {showLoader && <div className="notification-loading-bar" />}
          <EmptyLabel show={!isLoading} />
        </div>
      )}
    </animated.div>
  );
});

const colors: Record<NotificationIntent, string> = {
  DEFAULT: "hsl(216, 15%, 52%)",
  INFO: "hsl(209, 62%, 50%)",
  DANGER: "hsl(0, 76%, 57%)",
  WARNING: "hsl(40, 92%, 46%)",
  SUCCESS: "hsl(148, 48%, 43%)",
};
const background: Record<NotificationIntent, string> = {
  DEFAULT: "hsl(216, 15%, 97%)",
  INFO: "hsl(209, 62%, 97%)",
  DANGER: "hsl(0, 76%, 97%)",
  WARNING: "hsl(46, 100%, 90%)",
  SUCCESS: "hsl(148, 48%, 97%)",
};

const Tab: FC<
  React.PropsWithChildren<{
    tabState: NotificationGroup;
    currentTab: NotificationGroup;
    tabCount?: number;
    iconName: IconName;
    setTabState(tab: NotificationGroup): void;
  }>
> = ({ tabState, currentTab, iconName, children, tabCount = 0, setTabState }) => {
  const isActive = tabState === currentTab;
  return (
    <li className={classNames(isActive && "is-active")}>
      <Tooltip label={`${t("commun_notification")} : ${tabCount}`} side="bottom" portalZindex={50}>
        <a
          aria-current={isActive ? "page" : undefined}
          className={tw("gap-2")}
          onClick={() => setTabState(currentTab)}
        >
          <span className="icon is-small">
            <Fa icon={["fad", iconName]} />
          </span>
          <span className={tw("relative pb-1")}>
            {children}
            {tabCount > 0 && (
              <span
                className={tw(
                  "absolute bottom-0 left-0 right-0 h-0.5 rounded-full",
                  isActive ? "bg-blue-500" : "bg-blue-400",
                )}
              />
            )}
          </span>
        </a>
      </Tooltip>
    </li>
  );
};

const NotificationCard: FC<
  React.PropsWithChildren<{
    id: string;
    title: string;
    actions?: AllNotificationAction[];
    isNew: boolean;
    createdAt: string;
    intent: NotificationIntent;
    validate: (id: string) => void;
  }>
> = ({ id, title, children, actions = [], isNew, createdAt, intent, validate }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // appelé au clic (div ou Link) ou après succès d'un processus : marque la notification comme traitée
  function validateNotification() {
    validate(id);
  }

  return (
    <div
      role="button"
      onClick={validateNotification}
      style={{
        cursor: "pointer",
      }}
    >
      <header className={classNames("has-text-weight-semibold", isNew && "is-italic")}>
        {title}
      </header>
      <div
        className={classNames("has-text-weight-light is-size-8", {
          "has-text-grey": isNew,
          "has-text-grey-light": isNew,
          "is-italic": isNew,
        })}
      >
        <div dangerouslySetInnerHTML={{ __html: children as string }} />
      </div>
      <div className="buttons" style={{ marginBottom: 0, marginTop: "0.25rem" }}>
        {actions.map((action, i) => {
          const classes = classNames("button is-small is-rounded ", {
            "is-link is-outlined": intent === "INFO",
            "is-success is-outlined": intent === "SUCCESS",
            "is-warning": intent === "WARNING",
            "is-danger is-outlined": intent === "DANGER",
          });
          const styles: CSSProperties = {
            borderStyle: "dashed",
          };

          if (action.type === "LINK") {
            const url = getNavigationUrlFromMap(action.params);
            return (
              <Link
                key={i}
                className={classes}
                to={url}
                onClick={validateNotification}
                style={styles}
              >
                {action.label}
              </Link>
            );
          } else if (action.type === "DIRECT_LINK") {
            return (
              <Link
                key={i}
                className={classes}
                to={action.url}
                onClick={validateNotification}
                style={styles}
              >
                {action.label}
              </Link>
            );
          } else if (action.type === "PROCESSUS") {
            const {
              apercu = false,
              entities,
              label,
              nature,
              rapide = false,
              sjmoCode,
              tableName,
            } = action.params;

            let type: ProcessusDefinitionNature;
            switch (nature) {
              case "EDITA":
              case "EDITS":
                type = "edition";
                break;
              case "JAVA":
              case "TRAIT":
                type = "traitement";
                break;
              case "NAEXT":
              case "NAINT":
                type = "navigation";
                break;
            }

            const definition = {
              id: id,
              type: type,
              label: label,
              affectation: ProcessusAffectation.GLOBAL,
              needEntity: true,
              apercu: type === "edition" ? apercu : undefined,
              rapide: type === "edition" ? rapide : undefined,
              forAll: type === "traitement" ? false : undefined,
              isAdvanced: type === "traitement" ? false : undefined,
              isOneByOne: false,
            } as ProcessusDefinition;

            return (
              <ProcessusProvider
                key={i}
                selected={entities}
                tableName={tableName}
                sjmoCode={sjmoCode}
                onAfterSaveProcess={validateNotification}
              >
                {isNew ? (
                  <ProcessusLink className={classes} definition={definition} style={styles} />
                ) : (
                  // désactivé une fois validé, pour éviter de relancer le traitement
                  <button className={classes} style={styles} disabled>
                    {label}
                  </button>
                )}
              </ProcessusProvider>
            );
          } else if (action.type === "DISPATCH") {
            const { type, ...payload } = action.params;
            return (
              <button
                key={i}
                className={classes}
                style={styles}
                onClick={() => {
                  dispatch({ type, payload: { ...payload, navigate } });
                  // on ne peut pas savoir si le dispatch a réussi : on valide quand même, sans désactiver
                  // le bouton, pour laisser la personne relancer en cas d'erreur
                  validateNotification();
                }}
              >
                {action.label}
              </button>
            );
          } else {
            return null;
          }
        })}
      </div>
      <div className={classNames(isNew && "is-italic")} style={{ fontSize: 10, marginTop: 2 }}>
        {formatDateRelative(createdAt, true)}
      </div>
    </div>
  );
};

const PanelWrapper: FC<React.PropsWithChildren> = ({ children }) => {
  const fadeApi = useSpringRef<{ opacity: number }>();
  const [fadeStyle] = useSpring(() => ({ ref: fadeApi, from: { opacity: 0 } }));

  useLayoutEffect(() => {
    fadeApi.start({ opacity: 1, config: { duration: 160 } });
  }, [fadeApi]);

  return <animated.div style={fadeStyle}>{children}</animated.div>;
};

function useDelayedLoader(isLoading: boolean, delay = 150): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setVisible(false);
      return;
    }
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [isLoading, delay]);

  return visible;
}

const EmptyLabel: FC<{ show: boolean }> = ({ show }) => {
  const transitions = useTransition(show, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 50 },
  });
  return transitions((style, visible) =>
    visible ? (
      <animated.div
        style={{
          ...style,
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <p style={{ fontSize: "0.875rem", color: "#4a4a4a", margin: 0 }}>
          {t("commun_aucune_notification_tab")}
        </p>
      </animated.div>
    ) : null,
  );
};
